import { useState, useEffect, useCallback } from 'react';
import { apiRequest, useAuthStore } from '../hooks/useAuthStore';
import { Settings, MessageSquare, Palette, Shield, Bell, Save, CheckCircle, AlertTriangle, Calendar } from 'lucide-react';
import clsx from 'clsx';

interface MinistryData {
  id: string;
  name: string;
  description: string | null;
  contact_email: string;
  contact_phone: string | null;
  country: string | null;
  timezone: string;
  language: string;
  welcome_message: string;
  bot_name: string;
  primary_color: string;
  secondary_color: string;
  whatsapp_phone_number_id: string | null;
  whatsapp_business_account_id: string | null;
  whatsapp_verify_token: string | null;
  status: string;
}

interface ScheduleConfig {
  session_duration_minutes: number;
  video_conference_link: string | null;
  available_days: number[] | string;
  start_time: string;
  end_time: string;
  timezone: string;
  buffer_minutes: number;
}

const DAY_NAMES = ['', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

export function SettingsPage() {
  const { user } = useAuthStore();
  const ministryId = user?.ministryId;

  const [activeSection, setActiveSection] = useState('general');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // General settings
  const [general, setGeneral] = useState({
    name: '', description: '', contactEmail: '', contactPhone: '', country: '', timezone: 'America/Bogota', language: 'es',
  });

  // WhatsApp settings
  const [whatsapp, setWhatsapp] = useState({
    phoneNumberId: '', accessToken: '', businessAccountId: '',
  });
  const [whatsappInfo, setWhatsappInfo] = useState({ webhookUrl: '', verifyToken: '', hasConfig: false });

  // Appearance settings
  const [appearance, setAppearance] = useState({
    welcomeMessage: '', botName: '', primaryColor: '#3498DB', secondaryColor: '#2C3E50',
  });

  // Security
  const [passwords, setPasswords] = useState({ current: '', new_: '', confirm: '' });

  // Schedule config
  const [schedule, setSchedule] = useState<ScheduleConfig>({
    session_duration_minutes: 60, video_conference_link: null,
    available_days: [1, 2, 3, 4, 5], start_time: '09:00', end_time: '17:00',
    timezone: 'America/Bogota', buffer_minutes: 15,
  });

  // Notification preferences
  const [notifications, setNotifications] = useState({
    newCandidate: true, phaseComplete: true, inactive: false, processComplete: false,
  });

  const fetchData = useCallback(async () => {
    if (!ministryId) return;
    setIsLoading(true);

    const [ministryRes, scheduleRes] = await Promise.all([
      apiRequest<MinistryData>(`/ministries/${ministryId}`),
      apiRequest<ScheduleConfig>(`/ministries/${ministryId}/schedule-config`),
    ]);

    if (ministryRes.success && ministryRes.data) {
      const m = ministryRes.data;
      setGeneral({
        name: m.name || '', description: m.description || '',
        contactEmail: m.contact_email || '', contactPhone: m.contact_phone || '',
        country: m.country || '', timezone: m.timezone || 'America/Bogota', language: m.language || 'es',
      });
      setAppearance({
        welcomeMessage: m.welcome_message || '', botName: m.bot_name || '',
        primaryColor: m.primary_color || '#3498DB', secondaryColor: m.secondary_color || '#2C3E50',
      });
      setWhatsappInfo({
        webhookUrl: `${window.location.origin}/api/v1/webhook/${m.id}`,
        verifyToken: m.whatsapp_verify_token || '',
        hasConfig: !!m.whatsapp_phone_number_id,
      });
      if (m.whatsapp_phone_number_id) {
        setWhatsapp(prev => ({ ...prev, phoneNumberId: m.whatsapp_phone_number_id || '', businessAccountId: m.whatsapp_business_account_id || '' }));
      }
    }

    if (scheduleRes.success && scheduleRes.data) {
      const s = scheduleRes.data;
      const days = typeof s.available_days === 'string' ? JSON.parse(s.available_days) : s.available_days;
      setSchedule({ ...s, available_days: days });
    }

    setIsLoading(false);
  }, [ministryId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setSaveMessage({ type, text });
    setTimeout(() => setSaveMessage(null), 3000);
  };

  const handleSaveGeneral = async () => {
    if (!ministryId) return;
    setIsSaving(true);
    const result = await apiRequest(`/ministries/${ministryId}`, {
      method: 'PUT',
      body: JSON.stringify(general),
    });
    if (result.success) showMessage('success', 'Configuración general guardada');
    else showMessage('error', 'Error al guardar configuración general');
    setIsSaving(false);
  };

  const handleSaveWhatsApp = async () => {
    if (!ministryId) return;
    setIsSaving(true);
    const result = await apiRequest<{ webhookUrl: string; verifyToken: string }>(`/ministries/${ministryId}/whatsapp`, {
      method: 'PUT',
      body: JSON.stringify(whatsapp),
    });
    if (result.success && result.data) {
      setWhatsappInfo({ webhookUrl: result.data.webhookUrl || whatsappInfo.webhookUrl, verifyToken: result.data.verifyToken || '', hasConfig: true });
      showMessage('success', 'WhatsApp configurado correctamente');
    } else {
      showMessage('error', 'Error al configurar WhatsApp');
    }
    setIsSaving(false);
  };

  const handleSaveAppearance = async () => {
    if (!ministryId) return;
    setIsSaving(true);
    const result = await apiRequest(`/ministries/${ministryId}`, {
      method: 'PUT',
      body: JSON.stringify(appearance),
    });
    if (result.success) showMessage('success', 'Apariencia guardada');
    else showMessage('error', 'Error al guardar apariencia');
    setIsSaving(false);
  };

  const handleChangePassword = async () => {
    if (passwords.new_ !== passwords.confirm) {
      showMessage('error', 'Las contraseñas no coinciden');
      return;
    }
    if (passwords.new_.length < 8) {
      showMessage('error', 'La contraseña debe tener al menos 8 caracteres');
      return;
    }
    setIsSaving(true);
    const result = await apiRequest('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword: passwords.current, newPassword: passwords.new_ }),
    });
    if (result.success) {
      setPasswords({ current: '', new_: '', confirm: '' });
      showMessage('success', 'Contraseña cambiada correctamente');
    } else {
      showMessage('error', 'Contraseña actual incorrecta');
    }
    setIsSaving(false);
  };

  const handleSaveSchedule = async () => {
    if (!ministryId) return;
    setIsSaving(true);
    const result = await apiRequest(`/ministries/${ministryId}/schedule-config`, {
      method: 'PUT',
      body: JSON.stringify({
        sessionDurationMinutes: schedule.session_duration_minutes,
        videoConferenceLink: schedule.video_conference_link,
        availableDays: schedule.available_days,
        startTime: schedule.start_time,
        endTime: schedule.end_time,
        timezone: schedule.timezone,
        bufferMinutes: schedule.buffer_minutes,
      }),
    });
    if (result.success) showMessage('success', 'Configuración de agendamiento guardada');
    else showMessage('error', 'Error al guardar agendamiento');
    setIsSaving(false);
  };

  const handleSave = () => {
    switch (activeSection) {
      case 'general': return handleSaveGeneral();
      case 'whatsapp': return handleSaveWhatsApp();
      case 'appearance': return handleSaveAppearance();
      case 'security': return handleChangePassword();
      case 'notifications': return showMessage('success', 'Preferencias guardadas');
      case 'schedule': return handleSaveSchedule();
    }
  };

  const toggleDay = (day: number) => {
    const days = schedule.available_days as number[];
    setSchedule({
      ...schedule,
      available_days: days.includes(day) ? days.filter(d => d !== day) : [...days, day].sort(),
    });
  };

  const sections = [
    { id: 'general', name: 'General', icon: Settings },
    { id: 'whatsapp', name: 'WhatsApp', icon: MessageSquare },
    { id: 'appearance', name: 'Apariencia', icon: Palette },
    { id: 'schedule', name: 'Agendamiento', icon: Calendar },
    { id: 'security', name: 'Seguridad', icon: Shield },
    { id: 'notifications', name: 'Notificaciones', icon: Bell },
  ];

  if (isLoading) {
    return <div className="flex justify-center py-12"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Configuración</h1>
        <div className="flex items-center gap-3">
          {saveMessage && (
            <div className={clsx('flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg', saveMessage.type === 'success' ? 'bg-success-50 text-success-700 dark:bg-success-500/10 dark:text-success-400' : 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400')}>
              {saveMessage.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
              {saveMessage.text}
            </div>
          )}
          <button onClick={handleSave} disabled={isSaving} className="btn-primary">
            {isSaving ? <span className="spinner mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar cambios
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="card p-2">
          <nav className="space-y-1">
            {sections.map((s) => (
              <button key={s.id} onClick={() => setActiveSection(s.id)} className={clsx('w-full', activeSection === s.id ? 'sidebar-item-active' : 'sidebar-item')}>
                <s.icon className="w-5 h-5" />
                <span>{s.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3 card p-6">
          {activeSection === 'general' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Configuración General</h2>
              <div>
                <label className="label">Nombre del Ministerio</label>
                <input type="text" className="input" value={general.name} onChange={(e) => setGeneral({ ...general, name: e.target.value })} />
              </div>
              <div>
                <label className="label">Descripción</label>
                <textarea className="input h-20" value={general.description} onChange={(e) => setGeneral({ ...general, description: e.target.value })} />
              </div>
              <div>
                <label className="label">Email de Contacto</label>
                <input type="email" className="input" value={general.contactEmail} onChange={(e) => setGeneral({ ...general, contactEmail: e.target.value })} />
              </div>
              <div>
                <label className="label">Teléfono de Contacto</label>
                <input type="tel" className="input" value={general.contactPhone} onChange={(e) => setGeneral({ ...general, contactPhone: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">País</label>
                  <select className="input" value={general.country} onChange={(e) => setGeneral({ ...general, country: e.target.value })}>
                    <option value="">Seleccionar...</option>
                    <option value="Colombia">Colombia</option>
                    <option value="México">México</option>
                    <option value="Argentina">Argentina</option>
                    <option value="Chile">Chile</option>
                    <option value="Perú">Perú</option>
                    <option value="Ecuador">Ecuador</option>
                    <option value="Venezuela">Venezuela</option>
                    <option value="España">España</option>
                    <option value="Estados Unidos">Estados Unidos</option>
                  </select>
                </div>
                <div>
                  <label className="label">Zona Horaria</label>
                  <select className="input" value={general.timezone} onChange={(e) => setGeneral({ ...general, timezone: e.target.value })}>
                    <option value="America/Bogota">America/Bogota (UTC-5)</option>
                    <option value="America/Mexico_City">America/Mexico_City (UTC-6)</option>
                    <option value="America/Argentina/Buenos_Aires">America/Buenos_Aires (UTC-3)</option>
                    <option value="America/Santiago">America/Santiago (UTC-4)</option>
                    <option value="America/Lima">America/Lima (UTC-5)</option>
                    <option value="America/Guayaquil">America/Guayaquil (UTC-5)</option>
                    <option value="America/Caracas">America/Caracas (UTC-4)</option>
                    <option value="Europe/Madrid">Europe/Madrid (UTC+1)</option>
                    <option value="America/New_York">America/New_York (UTC-5)</option>
                    <option value="America/Los_Angeles">America/Los_Angeles (UTC-8)</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'whatsapp' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Configuración de WhatsApp</h2>
              <div className="p-4 bg-warning-50 dark:bg-warning-500/10 rounded-lg">
                <p className="text-sm text-warning-700 dark:text-warning-400">
                  <strong>Nota:</strong> Necesitas una cuenta de WhatsApp Business API para usar esta funcionalidad.
                </p>
              </div>
              <div>
                <label className="label">Phone Number ID</label>
                <input type="text" className="input" placeholder="Ej: 123456789012345" value={whatsapp.phoneNumberId} onChange={(e) => setWhatsapp({ ...whatsapp, phoneNumberId: e.target.value })} />
              </div>
              <div>
                <label className="label">Access Token</label>
                <input type="password" className="input" placeholder={whatsappInfo.hasConfig ? '••••••••• (configurado)' : 'Tu access token de Meta'} value={whatsapp.accessToken} onChange={(e) => setWhatsapp({ ...whatsapp, accessToken: e.target.value })} />
              </div>
              <div>
                <label className="label">Business Account ID (opcional)</label>
                <input type="text" className="input" placeholder="Ej: 123456789012345" value={whatsapp.businessAccountId} onChange={(e) => setWhatsapp({ ...whatsapp, businessAccountId: e.target.value })} />
              </div>
              {whatsappInfo.hasConfig && (
                <div className="space-y-3 p-4 bg-gray-50 dark:bg-dark-700 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">URL del Webhook</p>
                    <code className="text-sm text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-500/10 px-2 py-1 rounded block break-all">
                      {whatsappInfo.webhookUrl}
                    </code>
                  </div>
                  {whatsappInfo.verifyToken && (
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Verify Token</p>
                      <code className="text-sm text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-500/10 px-2 py-1 rounded block break-all">
                        {whatsappInfo.verifyToken}
                      </code>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeSection === 'appearance' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Apariencia</h2>
              <div>
                <label className="label">Mensaje de Bienvenida</label>
                <textarea className="input h-32" value={appearance.welcomeMessage} onChange={(e) => setAppearance({ ...appearance, welcomeMessage: e.target.value })} />
              </div>
              <div>
                <label className="label">Nombre del Bot</label>
                <input type="text" className="input" value={appearance.botName} onChange={(e) => setAppearance({ ...appearance, botName: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Color Primario</label>
                  <div className="flex gap-2">
                    <input type="color" className="w-12 h-10 rounded cursor-pointer" value={appearance.primaryColor} onChange={(e) => setAppearance({ ...appearance, primaryColor: e.target.value })} />
                    <input type="text" className="input" value={appearance.primaryColor} onChange={(e) => setAppearance({ ...appearance, primaryColor: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className="label">Color Secundario</label>
                  <div className="flex gap-2">
                    <input type="color" className="w-12 h-10 rounded cursor-pointer" value={appearance.secondaryColor} onChange={(e) => setAppearance({ ...appearance, secondaryColor: e.target.value })} />
                    <input type="text" className="input" value={appearance.secondaryColor} onChange={(e) => setAppearance({ ...appearance, secondaryColor: e.target.value })} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'schedule' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Agendamiento de Sesiones</h2>
              <div>
                <label className="label">Días Disponibles</label>
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 4, 5, 6, 7].map(day => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(day)}
                      className={clsx(
                        'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                        (schedule.available_days as number[]).includes(day)
                          ? 'bg-primary-500 text-white'
                          : 'bg-gray-100 text-gray-600 dark:bg-dark-700 dark:text-gray-400'
                      )}
                    >
                      {DAY_NAMES[day]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Hora de Inicio</label>
                  <input type="time" className="input" value={schedule.start_time} onChange={(e) => setSchedule({ ...schedule, start_time: e.target.value })} />
                </div>
                <div>
                  <label className="label">Hora de Fin</label>
                  <input type="time" className="input" value={schedule.end_time} onChange={(e) => setSchedule({ ...schedule, end_time: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Duración de Sesión (minutos)</label>
                  <input type="number" className="input" value={schedule.session_duration_minutes} onChange={(e) => setSchedule({ ...schedule, session_duration_minutes: parseInt(e.target.value) || 60 })} />
                </div>
                <div>
                  <label className="label">Buffer entre Sesiones (minutos)</label>
                  <input type="number" className="input" value={schedule.buffer_minutes} onChange={(e) => setSchedule({ ...schedule, buffer_minutes: parseInt(e.target.value) || 15 })} />
                </div>
              </div>
              <div>
                <label className="label">Link de Videoconferencia</label>
                <input type="url" className="input" placeholder="https://meet.google.com/... o https://zoom.us/..." value={schedule.video_conference_link || ''} onChange={(e) => setSchedule({ ...schedule, video_conference_link: e.target.value || null })} />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Se enviará automáticamente al candidato al agendar sesión virtual.</p>
              </div>
            </div>
          )}

          {activeSection === 'security' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Cambiar Contraseña</h2>
              <div>
                <label className="label">Contraseña Actual</label>
                <input type="password" className="input" placeholder="Contraseña actual" value={passwords.current} onChange={(e) => setPasswords({ ...passwords, current: e.target.value })} />
              </div>
              <div>
                <label className="label">Nueva Contraseña</label>
                <input type="password" className="input" placeholder="Nueva contraseña (mín. 8 caracteres)" value={passwords.new_} onChange={(e) => setPasswords({ ...passwords, new_: e.target.value })} />
              </div>
              <div>
                <label className="label">Confirmar Nueva Contraseña</label>
                <input type="password" className="input" placeholder="Confirmar nueva contraseña" value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} />
              </div>
              {passwords.new_ && passwords.confirm && passwords.new_ !== passwords.confirm && (
                <p className="text-sm text-red-600 dark:text-red-400">Las contraseñas no coinciden</p>
              )}
            </div>
          )}

          {activeSection === 'notifications' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notificaciones</h2>
              {[
                { key: 'newCandidate' as const, title: 'Nuevo candidato', desc: 'Cuando un candidato inicia el proceso' },
                { key: 'phaseComplete' as const, title: 'Candidato completa fase', desc: 'Cuando un candidato termina una fase' },
                { key: 'inactive' as const, title: 'Candidato inactivo', desc: 'Cuando un candidato no interactúa en 7 días' },
                { key: 'processComplete' as const, title: 'Proceso completado', desc: 'Cuando un candidato termina todo el proceso' },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-700 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{item.title}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={notifications[item.key]}
                      onChange={() => setNotifications({ ...notifications, [item.key]: !notifications[item.key] })}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-dark-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
